import { Box, Typography } from "@mui/material";
import type { FacilityPromotionSummary } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";

const SERVICE = {
  court_reservation: "Court",
  open_play: "Drop-in",
  classes: "Class",
  coaching: "Coaching",
} as const;

/**
 * "COURT · 50% OFF": the service, plus the discount when the summary states one.
 * Earned rewards say so first, so they never read as an open discount.
 */
export function offerOverline(promotion: FacilityPromotionSummary): string {
  const match = /(\d+(?:\.\d+)?)%\s+off/i.exec(promotion.summary);
  const percent = match ? `${Number(match[1])}% off` : null;
  const kind =
    promotion.audience === "earned_referral"
      ? "Referral reward"
      : promotion.audience === "earned_loyalty"
        ? `${SERVICE[promotion.service]} loyalty reward`
        : SERVICE[promotion.service];
  return [kind, percent].filter(Boolean).join(" · ");
}

/** When an offer is not simply available, the one condition a player must know. */
export function offerCondition(promotion: FacilityPromotionSummary): string | null {
  if (promotion.audience === "earned_referral") return "Earn after your friend’s verified visit";
  if (promotion.audience === "earned_loyalty") {
    return `Earn after ${promotion.reward_threshold ?? "the required"} verified services`;
  }
  return promotion.eligibility === "ineligible" ? "See offer terms" : null;
}

/** One compact offer in a swipe row: overline, offer name, then one line of context. */
export function OfferCard({
  promotion,
  to,
  subtitle,
  detail = false,
}: {
  promotion: FacilityPromotionSummary;
  to: string;
  subtitle: string;
  /** Show the subtitle over two lines (the offer's terms) instead of one. */
  detail?: boolean;
}) {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        flex: "0 0 auto",
        width: detail ? 260 : 220,
        scrollSnapAlign: "start",
        p: 1.25,
        border: 1,
        borderColor: "divider",
        borderRadius: 2.5,
        color: "inherit",
        textDecoration: "none",
        "&:hover": { borderColor: "primary.main" },
        "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "primary.main", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}
      >
        {offerOverline(promotion)}
      </Typography>
      <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.9375rem" }}>{promotion.headline}</Typography>
      {offerCondition(promotion) ? (
        <Typography noWrap variant="caption" sx={{ display: "block", fontWeight: 600 }}>
          {offerCondition(promotion)}
        </Typography>
      ) : null}
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap={!detail}
        sx={detail ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } : undefined}
      >
        {subtitle}
      </Typography>
    </Box>
  );
}

/** Horizontal swipe row for OfferCards. */
export function OfferRow({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{ display: "flex", gap: 1, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", px: { xs: 0.5, sm: 0 } }}
    >
      {children}
    </Box>
  );
}
